import React, { useState } from "react";
import { EditOutlined, UndoOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { SbButton } from "uiCore";
import { SizedInputNumberStyled } from "../orgDesign.styles";
import { formatUsd, getBudgetOverview, getBudgetVarianceNote } from "../orgDesign.constants";
import { BarStyled, CardStyled, NumStyled, LabelStyled, EditingRowStyled, VarianceNoteStyled } from "./OrgDesignBudgetBar.styles";

// Projected Budget: green at or under target, red over it.
const PROJECTED_TONE = {
  within: { bg: "#f0fdf4", color: "#16a34a" },
  over: { bg: "#fef2f2", color: "#dc2626" },
};

const OrgDesignBudgetBar = ({ baselineBudget, targetBudget, projectedBudget, onSetTargetBudget, syncing }) => {
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(Math.round(targetBudget ?? baselineBudget));

  // Same calculation as the PDF's Summary page, so the two always agree.
  const { effectiveTarget, variance, isOverTarget } = getBudgetOverview(baselineBudget, targetBudget, projectedBudget);
  // The note and the Projected colour are held back with the Projected
  // figure while a sync is in flight, so neither describes a number that's
  // about to change. Neutral too until there's a projected figure to compare
  // (as on the PDF).
  const hasSettledVariance = !syncing && variance != null;
  const varianceNote = hasSettledVariance ? getBudgetVarianceNote(variance) : null;
  const projectedTone = hasSettledVariance ? PROJECTED_TONE[isOverTarget ? "over" : "within"] : null;

  const startEditing = () => {
    setDraftValue(Math.round(effectiveTarget));
    setEditing(true);
  };
  const commit = () => {
    if (draftValue == null || Number.isNaN(draftValue)) return;
    setEditing(false);
    onSetTargetBudget(draftValue);
  };
  const cancel = () => setEditing(false);

  return (
    <>
      <BarStyled>
        <CardStyled>
          <NumStyled>{formatUsd(baselineBudget)}</NumStyled>
          <LabelStyled>Baseline Budget</LabelStyled>
        </CardStyled>

        <CardStyled>
          {editing ? (
            <EditingRowStyled>
              <SizedInputNumberStyled
                $width={120}
                aria-label="Target budget"
                size="small"
                min={0}
                step={10000}
                value={draftValue}
                onChange={setDraftValue}
                onPressEnter={commit}
                // en-US to match the parser, which strips only "$" and ",".
                // A browser-locale format (e.g. de-DE's "1.500.000") would
                // otherwise parse back as 1.5.
                formatter={(value) => `$${Math.round(value).toLocaleString("en-US")}`}
                parser={(value) => value.replace(/[$,]/g, "")}
                autoFocus
              />
              <SbButton buttonType="action" size="small" icon={<SaveOutlined />} title="Save" onClick={commit} disabled={draftValue == null || Number.isNaN(draftValue)} />
              <SbButton buttonType="action" size="small" icon={<CloseOutlined />} title="Cancel" onClick={cancel} />
            </EditingRowStyled>
          ) : (
            <NumStyled>
              {formatUsd(effectiveTarget)}
              <SbButton buttonType="action" size="small" icon={<EditOutlined />} title="Edit target budget" onClick={startEditing} />
              {targetBudget != null && (
                <SbButton buttonType="action" size="small" icon={<UndoOutlined />} title="Reset to baseline" onClick={() => onSetTargetBudget(null)} />
              )}
            </NumStyled>
          )}
          <LabelStyled>Target Budget{targetBudget == null ? " (= baseline)" : ""}</LabelStyled>
        </CardStyled>

        <CardStyled $bg={projectedTone?.bg}>
          {/* Blank instead of the stale local number while a server sync is
              in flight, to avoid a flicker right before the real figure lands. */}
          <NumStyled $color={projectedTone?.color}>{syncing ? "" : formatUsd(projectedBudget)}</NumStyled>
          <LabelStyled>Projected Budget{syncing ? " · Syncing…" : ""}</LabelStyled>
        </CardStyled>
      </BarStyled>
      {varianceNote && <VarianceNoteStyled>{varianceNote}</VarianceNoteStyled>}
    </>
  );
};

export default OrgDesignBudgetBar;
